import React, { useEffect, useState } from 'react';
import { CardContent } from "@/components/ui/card";
import { useDashboardData } from "@/services/dashboardDataService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Skeleton } from "@/components/ui/skeleton";
import "./widgets.css";
import { cleanDateAndTimeObject } from '@shared/renderDate';

interface OverdueTask {
  id: string;
  assID?: string;
  dueDate: Date;
  description: string;
  checkDetail?: {
    check: string;
    [key: string]: any;
  };
}

const OverdueTasksWidgetMediumTall: React.FC = () => {
  const { data, isLoading, error, metadata } = useDashboardData('overdue-tasks');
  const { activeTenancy } = useTenancy();
  const [tasks, setTasks] = useState<OverdueTask[]>([]);
  
  // Transform the data when it's loaded
  useEffect(() => {
    if (data?.overdue) {
      const companyName = activeTenancy?.name || 'your organization';
      
      const formattedTasks = data.overdue.map((task: any) => ({
        id: task.id || task.assID || `task-${Math.random().toString(36).substr(2, 9)}`,
        assID: task.assID || task.id,
        title: task.checkDetail?.check || 'Overdue Task',
        dueDate: task.dueDate?.toDate ? task.dueDate.toDate() : cleanDateAndTimeObject(task.dueDate || Date.now()),
        description: task.description || `Task for ${companyName}`,
        status: 'overdue',
      }));
      
      setTasks(formattedTasks);
    }
  }, [data, activeTenancy?.name]);

  const handleTaskClick = (assID: string) => {
    window.location.href = `assurance-actions.html?assID=${assID}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Overdue Tasks</h3>
        <div className="flex flex-col items-center justify-center py-4 w-full">
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </CardContent>
    );
  }

  // Error state
  if (error || metadata?.error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Overdue Tasks</h3>
        <div className="flex flex-col items-center justify-center py-4">
          <p className="text-red-500 text-sm text-center">
            {error || metadata?.message || 'Failed to load overdue tasks'}
          </p>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size' }}>
      <div className="w-full flex justify-between items-center mb-2 flex-shrink-0">
        <h3 className="text-lg font-semibold truncate pr-2">Overdue Tasks</h3>
        {metadata?.lastUpdated && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {cleanDateAndTimeObject(metadata.lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <div className="flex-1 overflow-hidden flex flex-col">
        {tasks.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-4">
            <h1 style={{ fontSize: "2rem" }}>🎉</h1>
            <p className="text-sm text-muted-foreground text-center px-2">
              {metadata?.collectionTriggered 
                ? 'Data collection in progress...'
                : 'No overdue tasks'}
            </p>
          </div>
        ) : (
          <div className="overflow-y-auto pr-1 -mr-1">
            <ul className="w-full flex flex-col gap-2">
              {tasks.map((task) => (
                <li
                  key={task.assID}
                  className="rounded-md border border-border bg-background p-2 cursor-pointer hover:bg-accent transition"
                  onClick={() => handleTaskClick(task.assID)}
                >
                  <div className="font-medium text-xs flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold truncate">
                        {task.description}
                      </span>
                    </div>
                    <div className="text-muted-foreground text-xs">
                      <span className="whitespace-nowrap">
                        Due: {task.dueDate.toLocaleDateString(undefined, { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {' '}
                        <span className="text-red-600">
                          ({Math.ceil((new Date().getTime() - task.dueDate.getTime()) / (1000 * 60 * 60 * 24))}d)
                        </span>
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </CardContent>
  );
};

export default OverdueTasksWidgetMediumTall;
